import { z } from "zod";

const normalizeOptionalUrl = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;

  const trimmed = String(value).trim();

  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

const OptionalWebsiteUrlSchema = z.preprocess(
  normalizeOptionalUrl,
  z
    .union([z.string().url("Please enter a valid URL"), z.literal("")])
    .optional(),
);

export const GatheringSchema = z.object({
  id: z.string().optional(),
  hostID: z.string().optional(),
  shortcode: z
    .string()
    .min(4, "Shortcode must be at least 4 characters long.")
    .max(32, "Shortcode must be no more than 32 characters long."),
  gatheringName: z
    .string()
    .min(5, "Gathering Name must be at least 5 characters long.")
    .max(30, "Gathering Name must be less than 30 characters long."),
  description: z
    .string()
    .max(200, "Description must be less than 200 characters long."),
  location: z
    .string()
    .min(1, "Location must be provided.")
    .max(100, "Location must be less than 100 characters long."),
  startDate: z.string().min(1, "Start Date must be provided."),
  endDate: z.string().min(1, "End Date must be provided."),
  published: z.boolean().default(false),
  visible: z.boolean().default(false),
  dynamicLink: z.string().optional(),
  gatheringReferenceID: z.string().optional(),
  hostName: z.string().optional(),
  hostPhoneNum: z.string().optional(),
  gatheringWebsite: OptionalWebsiteUrlSchema,
  medicPhoneNum: z.string().optional(),
  securityPhoneNum: z.string().optional(),
  hostEmail: z.string().email().optional(),
  contactEmail: z.string().email().optional(),
  customLinkName: z.string().optional(),
  customLinkUrl: OptionalWebsiteUrlSchema,
  mapImage: z.string().nullable().optional(),
  logoImage: z.string().nullable().optional(),
  primaryColor: z.string().optional(),
  secondaryColor: z.string().optional(),
  vendors: z.array(z.string()).optional(),
  teamEmails: z.array(z.string()).optional().default([]),
  priorityVendors: z.array(z.string()).optional().default([]),
  approvedEmbedUrl: z.string().url().optional(), // New field
  embedSize: z.string().optional(), // New field
  referredCode: z.string().nullable().optional(),
  lastModGathering: z.any().optional(),
  lastModVendors: z.any().optional(),
  lastModSchedule: z.any().optional(),
  locationSortMethod: z.enum(["manual", "alphabetical", "count"]).optional(),
  locationOrder: z.array(z.string()).optional(),
});

export type GatheringSchema = z.infer<typeof GatheringSchema>;
